/**
 * MobileTaskbar Component
 *
 * Bottom navigation bar for mobile devices
 * Shows 5 main navigation items: Home, Devices, AI Assistant, Optimizations, Alarms
 * Includes notification badge for unread security vulnerabilities.
 */

import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HouseIcon } from './HouseIcon';
import { DevicesIcon } from './DevicesIcon';
import { AiAssistantIcon } from './AiAssistantIcon';
import { BellIcon } from './BellIcon';
import { NotificationBadge } from './NotificationBadge';
import { useActiveAlarmCount } from '@/features/alarms';
import { getProfileRole } from '@/services/auth/auth.service';
import styles from './MobileTaskbar.module.css';

interface NavItem {
  href: string;
  icon: React.ElementType;
  labelKey: string;
}

const homeownerNavItems: NavItem[] = [
  { href: '/dashboard', icon: HouseIcon, labelKey: 'mobileTaskbar.home' },
  { href: '/devices', icon: DevicesIcon, labelKey: 'mobileTaskbar.devices' },
  { href: '/ai-assistant', icon: AiAssistantIcon, labelKey: 'mobileTaskbar.aiAssistant' },
  { href: '/optimizations', icon: Lightbulb, labelKey: 'mobileTaskbar.optimizations' },
  { href: '/alarms', icon: BellIcon, labelKey: 'mobileTaskbar.alarms' },
];

const technicianNavItems: NavItem[] = [
  { href: '/service-provider/dashboard', icon: HouseIcon, labelKey: 'mobileTaskbar.home' },
  { href: '/service-provider/devices', icon: DevicesIcon, labelKey: 'mobileTaskbar.devices' },
  { href: '/service-provider/ai-assistant', icon: AiAssistantIcon, labelKey: 'mobileTaskbar.aiAssistant' },
  { href: '/service-provider/optimizations', icon: Lightbulb, labelKey: 'mobileTaskbar.optimizations' },
  { href: '/service-provider/alarms', icon: BellIcon, labelKey: 'mobileTaskbar.alarms' },
];

// Pages where dark mode should be enabled
const DARK_MODE_PAGES = ['/ai-assistant', '/service-provider/ai-assistant'];

interface MobileTaskbarProps {
  dark?: boolean; // Optional prop to force dark mode
}

export function MobileTaskbar({ dark }: MobileTaskbarProps = {}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    getProfileRole().then(setRole);
  }, []);

  const navItems = role === 'technician' ? technicianNavItems : homeownerNavItems;
  const alarmsHref = role === 'technician' ? '/service-provider/alarms' : '/alarms';

  // Get active alarm counts for badge
  const { counts, highestSeverity } = useActiveAlarmCount();

  // Determine if dark mode should be active
  const isDark = dark ?? DARK_MODE_PAGES.includes(location.pathname);

  return (
    <nav className={`${styles.taskbar} ${isDark ? styles.dark : ''}`}>
      {navItems.map((item) => (
        <NavLink
          key={item.href}
          to={item.href}
          className={({ isActive }) =>
            `${styles.navItem} ${isActive ? styles.active : ''}`
          }
        >
          {({ isActive }) => (
            <span className={styles.navItemContent}>
              <item.icon size={24} className={styles.icon} />
              {/* Show notification badge on Alarms icon */}
              {item.href === alarmsHref && (
                <NotificationBadge
                  count={counts.total}
                  severity={highestSeverity}
                />
              )}
              {isActive && <span className={styles.label}>{t(item.labelKey)}</span>}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export default MobileTaskbar;
